import { useState, useEffect, useCallback } from "react";
import DataTable, {
  Column,
} from "../../../components/common/data-table/DataTable";
import { formatDate } from "../../../utils/date";
import styles from "../Products.module.scss";
import DeleteModal from "../../../components/common/delete-modal/DeleteModal";
import { useNavigate } from "react-router-dom";
import ProductsService from "../../../api/services/products.service";
import { ProductsTypes } from "../../../api/types/products.types";
import Loading from "../../../components/common/loading/Loading";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import i18n from "../../../i18n";

const UnsavedProducts = () => {
  const { t } = useTranslation("translation");
  const [deleteModal, setDeleteModal] = useState(false);
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [count, setCount] = useState<number | null>(null);
  const [products, setProducts] = useState<ProductsTypes[]>([]);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);

  const getProducts = useCallback(async () => {
    setLoading(true);
    try {
      const res = await ProductsService.getAllProducts(page, limit);
      setProducts(res.data.data);
      setCount(res.data.count);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      getProducts();
    }, 100);

    return () => clearTimeout(timer);
  }, [getProducts]);

  const handlePageChange = (newPage: number) => {
    if (newPage < 1) return;
    if (count && newPage > Math.ceil(count / limit)) return;
    setPage(newPage);
  };

  const handleLimitChange = (newLimit: number) => {
    setLimit(newLimit);
    setPage(1);
  };

  const handleDelete = async (id: number) => {
    try {
      await ProductsService.deleteProduct(id);
      setDeleteModal(false);
      getProducts();
    } catch (error: any) {
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        t("error_message");
      toast.error(message);
    }
  };

  const columns: Column[] = [
    {
      key: "category",
      header: t("columns.category") || "Category",
      render: (v) => {
        const lang = i18n.language;
        if (lang === "az" && v?.name_az) return v.name_az;
        if (lang === "en" && v?.name_en) return v.name_en;
        if (lang === "es" && v?.name_es) return v.name_es;
        if (lang === "de" && v?.name_de) return v.name_de;
        return v?.name || "Unnamed Category";
      },
    },
    {
      key: "currency",
      header: t("columns.currency") || "Currency",
      render: (v) => v?.name,
    },
    {
      key: "brand",
      header: t("columns.brand") || "Brand",
      render: (v) => v?.name,
    },
    {
      key: "created",
      header: t("columns.date") || "Date",
      render: (v) => formatDate(v, true),
    },
  ];

  if (loading) return <Loading />;

  if (products.length === 0) return null;

  return (
    <div className={styles.products}>
      <div className={styles.section_header}>
        <h2 className={styles.title}>{t("uncompleted_products")}</h2>
      </div>
      <DataTable
        data={products}
        columns={columns}
        actions
        onDelete={(id) => {
          setDeleteModal(true);
          setDeleteId(id);
        }}
        onEdit={(id) => navigate(`/dashboard/products/edit/${id}`)}
        paginationData={{
          total: count,
          page,
          limit,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      />
      <DeleteModal
        deleteModal={deleteModal}
        onClose={() => setDeleteModal(false)}
        onSubmit={() => handleDelete(Number(deleteId))}
        title={t("delete_uncompleted_product") || ""}
      />
    </div>
  );
};

export default UnsavedProducts;
